import React, { useState, useEffect } from 'react';
import { A, TextButton, Spinner } from 't-lib-ui/widgets';
import { sanitizePlainText } from 't-lib-ui/connector';
import { ModalDialog } from 't-lib-ui/structure';
import { RestCatalog, RestUserFeed } from '../schema-copy/api-schema';
import { getFeedIconUrl } from '../schema-copy/feed-icon';
import { CategoryIcon } from './CategoryIcons';
import { formatCategory, formatLastPostTime, formatFrequency, formatPostCount } from '../utils/formatting';
import FeedPreview, { StaleFetchWarning } from './FeedPreview';
import { useFeedNameOverride } from '../connector/feed-name-overrides';
import { useFeedQuota } from '../connector/feed-quota';
import { addIcon } from './icons';
import './PreviewFeedPopup.css';

interface PreviewFeedPopupProps {
  show: boolean;
  feed: RestUserFeed | RestCatalog | null;
  onClose: () => void;
  closeFocus?: string;
  subscribed?: boolean;
  onSubscribe?: () => Promise<void>;
}

const truncate = (text: string, max: number): string =>
  text.length > max ? text.slice(0, max - 1).trimEnd() + '…' : text;

const PreviewFeedPopup: React.FC<PreviewFeedPopupProps> = ({ show, feed, onClose, closeFocus, subscribed = false, onSubscribe }) => {
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [error, setError] = useState(false);
  const customFeedName = useFeedNameOverride(feed?.id);
  const { atLimit: atFeedQuota } = useFeedQuota();

  useEffect(() => {
    if (!show) {
      setError(false);
      setIsSubscribing(false);
    }
  }, [show]);

  if (!feed) return null;

  const handleSubscribe = async () => {
    if (!onSubscribe) return;
    setIsSubscribing(true);
    setError(false);
    try {
      await onSubscribe();
      onClose();
    } catch (err) {
      setError(true);
    } finally {
      setIsSubscribing(false);
    }
  };

  const imageUrl = getFeedIconUrl(feed, 192);
  const siteHostname = (() => {
    if (!feed.siteUrl) return '';
    try {
      return new URL(feed.siteUrl).hostname.replace(/^www\d?\./, '');
    } catch {
      return feed.siteUrl;
    }
  })();

  return (
    <ModalDialog
      show={show}
      title="Feed Preview"
      onClose={onClose}
      minWidth="500px"
      closeFocus={closeFocus}
      className="previewFeedPopup"
      closeButton="close"
      closeOnBackgroundClick={true}
    >
      <div className="previewFeedPopupContent">
        <div className="previewFeedHeader withImage">
          <img
            src={imageUrl}
            alt={feed.title}
            className={`previewFeedImage${feed.circularIcon ? ' feedIconCircular' : ''}`}
          />
          <div className="previewFeedHeaderBody">
            <div className="previewFeedHeaderTop">
              <div className="previewFeedHeaderText">
                <h3 className="previewFeedTitle">{customFeedName ?? feed.shortTitle ?? feed.title}</h3>
                {feed.siteUrl && (
                  <p className="previewFeedUrl">
                    <A href={feed.siteUrl} target="_blank" rel="noopener noreferrer">
                      {siteHostname}
                    </A>
                  </p>
                )}
              </div>
              <div className="previewFeedHeaderTools">
                {feed.paid === true && (
                  <span className="previewFeedDollar">$</span>
                )}
              </div>
            </div>
            {(feed.description || ('catalogDescription' in feed && feed.catalogDescription)) && (
              <div className="previewFeedDescriptions">
                {feed.description && (
                  <p className="previewFeedDescription">
                    {truncate(sanitizePlainText(feed.description), 240)}
                  </p>
                )}
                {'catalogDescription' in feed && feed.catalogDescription && (
                  <p className="previewFeedCatalogDescription">
                    {truncate(feed.catalogDescription, 240)}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="previewFeedMetaRow">
          <div className="previewFeedMetadata">
            {feed.category && (
              <span className="previewFeedMetaItem previewFeedMetaCategory">
                <CategoryIcon category={feed.category} size={16} color="var(--gray-400)" />
                {formatCategory(feed.category)}
              </span>
            )}
            {feed.postCount < 100 && (
              <span className="previewFeedMetaItem"><strong>{formatPostCount(feed.postCount)}</strong> available</span>
            )}
            {(() => {
              if (!feed.postFrequency) return null;
              if (!('dateAdded' in feed)) return null;
              const dateAdded = new Date(feed.dateAdded).getTime();
              const fortyEightHoursAgo = Date.now() - (48 * 60 * 60 * 1000);
              if (dateAdded < fortyEightHoursAgo)
                return <span className="previewFeedMetaItem">{formatFrequency(feed.postFrequency)}</span>;
              return null;
            })()}
            {feed.lastNewPostPubDate && (
              <span className="previewFeedMetaItem">Last post {formatLastPostTime(feed.lastNewPostPubDate)}</span>
            )}
          </div>

          {!subscribed && onSubscribe && (
            <div className="previewFeedAction">
              <TextButton
                label="Follow"
                icon={addIcon}
                type="major"
                size="small"
                onClick={handleSubscribe}
                enabled={isSubscribing || error || atFeedQuota ? 'disabled' : 'enabled'}
              />
              {isSubscribing && <Spinner />}
              {error && (
                <span className="previewFeedErrorText">
                  Failed to follow.
                </span>
              )}
            </div>
          )}
        </div>

        {feed.postCount > 0 && (
          <div className="previewFeedPosts">
            <FeedPreview feed={feed} maxPosts={10} />
          </div>
        )}
        {feed.postCount == 0 && (
          <>
            {/* With posts the warning is rendered by the embedded FeedPreview */}
            <StaleFetchWarning lastFetchedAt={feed.lastFetchedAt} />
            <p className="previewFeedNoPosts">No posts available for preview.</p>
          </>
        )}
      </div>
    </ModalDialog>
  );
};

export default PreviewFeedPopup;
